import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import { DataMapEditor } from '@mescius/wijmo.grid';
import * as wjcFilter from '@mescius/wijmo.grid.filter';
import { ConditionFilter, FilterCondition } from '@mescius/wijmo.grid.filter';
import * as wjcXlsx from '@mescius/wijmo.grid.xlsx';
import * as wjcPdf from '@mescius/wijmo.grid.pdf';

import { MarWijmoGridLayoutState, WijmoGridStateService } from '../../services/wijmo-grid-state.service';
import { MarWijmoColumnDef, MarWijmoTableOptions } from '../../models/mar-wijmo-table';
import { WjFlexGridFilter } from '@mescius/wijmo.angular2.grid.filter';
import { WjMultiSelect } from '@mescius/wijmo.angular2.input';

export interface CallbackEditEnding {
  binding: string;
  previousData: any;
  dataItem: any;
}

// Update condition filter for MultiSelect editor column
ConditionFilter.prototype.apply = function (e) {
  var t = this._col,
    i = this._c1,
    l = this._c2,
    r = !1,
    n = !1;
  if (!t || !t._binding || !this.isActive) return !0;
  e = t._binding.getValue(e);
  var o = this.dataMap || t.dataMap;
  if (Array.isArray(e)) {
    e = e.join();
  }
  if (o) e = o.getDisplayValue(e);
  else if (wjcCore.isDate(e)) {
    r = !this._hasTimePart();
    n = !this._hasDatePart();
    var a = FilterCondition._refDateTime;
    r
      ? (e = wjcCore.DateTime.fromDateTime(e, a))
      : n && (e = wjcCore.DateTime.fromDateTime(a, e));
  } else if (wjcCore.isNumber(e)) {
    var s = wjcCore.Globalize,
      u = t.format,
      d = s.formatNumber(e, u);
    e = s.parseFloat(d, u);
  }
  var c = i.apply(e, r, n),
    p = l.apply(e, r, n);
  return i.isActive && l.isActive
    ? this._and
      ? c && p
      : c || p
    : i.isActive
      ? c
      : !l.isActive || p;
};

@Component({
  selector: ' mar-wijmo-table',
  standalone: false,
  templateUrl: './mar-wijmo-table.component.html',
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: calc(100vh - 48px);
    }

    .h-full {
      height: calc(100vh - 48px);
    }

    .w-full {
      width: 100%;
    }

    .items-center {
      align-items: center;
    }

    .gap-8 {
      gap: 8px;
    }

    .flex {
      display: flex;
    }

    .flex-col {
      flex-direction: column;
    }

    .gap-2 {
      gap: 24px;
    }

    wj-flex-grid {
      height: 100%;
    }

    button {
      padding: 6px 10px;
      border-radius: 8px;
      border: 1px solid #ddd;
      background: #fff;
      cursor: pointer;
    }

    button:hover {
      background: #f5f5f5;
    }

    /* realce do FlexGridSearch */
    .wj-state-match {
      background: rgba(255, 235, 59, .5);
    }
  `]
})
export class MarWijmoTableComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @Output() callback = new EventEmitter();
  @Output() callbackEditEnding: EventEmitter<CallbackEditEnding> = new EventEmitter();
  @Input() items: any[] = [];
  @Input() state: any = {};
  @Input() columnsMultiselect: {key: any, value: any}[] = [];
  columns: MarWijmoColumnDef[] = [];
  id: number;

  @Input() options: MarWijmoTableOptions = {
    tableName: 'defaultWijmoTable',
    isReadOnly: true,
    allowPinning: false,
    frozenColumns: 0,
    showSearch: true,
    showGroupPanel: false,
    autoSizeOnInit: true,
    allowSorting: 0,
    exportBaseName: 'dados',
  };

  /** Congelar dinamicamente (espelha options.frozenColumns) */
  @Input() set frozenColumns(v: number) {
    this._frozenColumns = v ?? 0;
    if (this.grid) this.grid.frozenColumns = this._frozenColumns;
  }

  get frozenColumns() {
    return this._frozenColumns;
  }

  @Output() rowClicked = new EventEmitter<any>();
  @Output() filteredItems = new EventEmitter<any[]>();

  @ViewChild('gridRef', { static: true }) grid!: wjcGrid.FlexGrid;
  @ViewChild('gridRefFilter', { static: true }) gridRefFilter!: wjcFilter.FlexGridFilter;
  @ViewChildren(WjMultiSelect) multiSelects!: QueryList<WjMultiSelect>;

  cv!: wjcCore.CollectionView;
  private stateSvc = inject(WijmoGridStateService);

  private _frozenColumns = 0;
  private _valueFormatters = new Map<string, (val: any, item: any) => string>();

  constructor(
    private cdr: ChangeDetectorRef) {
  }


  ngOnInit(): void {
    // fonte de dados + notificação após filtro
    this.cv = new wjcCore.CollectionView(this.items);
    this.cv.collectionChanged.addHandler(() => {
      // dispara itens filtrados/agrupados atuais
      this.filteredItems.emit(this.cv.items);
    });

    // aplica frozen inicial
    this._frozenColumns = this.options.frozenColumns ?? 0;

    if (this.state?.columns) {
      this.stateSvc.apply(this.grid, this.gridRefFilter, this.state);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.cv && this.cv.sourceCollection && this.items?.length > 0) {
      this.cv.sourceCollection = this.items;
      this.cv.refresh();
    }
    if (changes['state']?.currentValue) {
      if (changes['state']?.currentValue?.columns) {
        try {
          this.onUpdate(changes);
        } catch (e) {
          console.log(e);
        }
      }
    }
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
  }

  // ============ Inicialização e layout ============
  onGridInit(grid: wjcGrid.FlexGrid) {
    // 1) construir colunas
    this.buildColumns(grid);

    // aplicar formatters por coluna (quando definidos em this._valueFormatters)
    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.cells) {
        const col = s.columns[e.col] as wjcGrid.Column;
        const fmt = this._valueFormatters.get(col.binding);
        if (fmt) {
          const item = s.rows[e.row]?.dataItem;
          const binding = new wjcCore.Binding(col.binding);
          const val = item != null ? binding.getValue(item) : null;
          e.cell.textContent = fmt(val, item);
        }
      }
    });

    // 3) restaurar layout salvo (ordem, largura, visibilidade, sort, filtros etc.)
    this.restoreLayout();

    // 4) auto size colunas (opcional)
    if (this.options.autoSizeOnInit) {
      setTimeout(() => this.autoSizeAllColumns(grid, true));
    }

    // 5) click na linha
    grid.hostElement.addEventListener('click', (e: MouseEvent) => {
      const ht = grid.hitTest(e);
      if (ht && ht.row >= 0 && ht.cellType === wjcGrid.CellType.Cell) {
        const item = grid.rows[ht.row]?.dataItem;
        if (item) this.rowClicked.emit(item);
      }
    });


    this.grid.pastedCell.addHandler((s, e: wjcGrid.CellRangeEventArgs) => {
      const col = s.columns[e.col];
      const row = s.rows[e.row];
      const item = row?.dataItem;

      this.callbackEditEnding.emit({
        binding: col.binding,
        previousData: e.data ? e.data.oldValue : null, // ou snapshot manual
        dataItem: item,
      });
    });
  }

  onUpdate(changes) {
    this.resetLayout();
    this.id = changes['state']?.currentValue?.id;
    this.columns = [...changes['state']?.currentValue?.columns];
    this.buildColumns(this.grid);
    this.gridRefFilter.filterDefinition = changes['state']?.currentValue?.filterDefinition;
    this.grid.frozenColumns = changes['state']?.currentValue?.frozenColumns;
    this.applyCollectionsFilter();
    this.grid.refresh();
  }


  initFilter(filter: WjFlexGridFilter) {
  }

  private applyCollectionsFilter() {
    setTimeout(() => {
      if (this.state?.columns) {
        this.state?.columns.filter(c => c.type === 'collection').forEach(col => {
          if (this.gridRefFilter.getColumnFilter(col.binding)) {
            // control sorting of values in filter dropdown listbox
            this.gridRefFilter.getColumnFilter(col.binding).valueFilter.sortValues = false;
            this.gridRefFilter.getColumnFilter(col.binding).valueFilter.apply = this.singleValueFilter();
            this.updateUniqueValues(this.gridRefFilter, col.binding);
          }
        });
      }
    }, 1000);
  }

  private updateUniqueValues(filter, colBinding) {
    let colFilter = filter?.getColumnFilter(colBinding);

    colFilter.valueFilter.uniqueValues = this.getUniqueValues(
      colFilter.valueFilter.getUniqueValues()
    );
  }

  private getUniqueValues(arr: any[]): string[] {
    const set = new Set<string>();

    arr.forEach((val) => {
      const value = val?.value;
      if (Array.isArray(value)) {
        value.forEach((item) => set.add(String(item)));
      } else if (typeof value === 'string') {
        set.add(value);
      } else if (value != null) {
        set.add(String(value));
      }
    });

    return Array.from(set);
  }

  singleValueFilter() {
    return function (value) {
      // default settings for filter (need not to be changed)
      var col = this.column;
      if (
        !col ||
        !col.binding ||
        !this.showValues ||
        !Object.keys(this.showValues).length
      ) {
        return true;
      }
      //
      value = value[col.binding];
      value = this.dataMap
        ? this.dataMap.getDisplayValue(value) || ''
        : col.dataMap
          ? col.dataMap.getDisplayValue(value) || ''
          : wjcCore.Globalize.format(value, col.format);

      // default setting ends here

      // apply conditions
      // split the string to create an array
      // and check if any of the value from the array is present or not
      for (let i = 0; i < value.length; i++) {
        if (this.showValues[value[i]] != undefined) {
          return true;
        }
      }
      return false;
    };
  }

  private autoSizeAllColumns(grid: wjcGrid.FlexGrid, header = true) {
    const first = 0;
    const last = grid.columns.length - 1;
    if (last >= first) {
      // assinatura: autoSizeColumns(firstColumn: number, lastColumn: number, header: boolean = false)
      grid.autoSizeColumns(first, last, header);
    }
  }

  onLoadedRows() {
  }

  // ============ Colunas e grupos ============
  private buildColumns(grid: wjcGrid.FlexGrid) {
    setTimeout(() => {
      grid.columns.clear();
      // sem grupos, criamos colunas simples
      this.columns.forEach(col => grid.columns.push(this.makeColumn(col)));

      // permite merge de cabeçalhos (caso queira criar níveis)
      grid.allowMerging = wjcGrid.AllowMerging.ColumnHeaders;
      grid.columnHeaders.rows.defaultSize = 36;
      grid.rows.defaultSize = 36;
      grid.refresh();
    });
  }

  getMultiSelectByBinding(binding: string): WjMultiSelect {
    const id = 'multiSelect' + binding;
    return this.multiSelects.find(ms => ms.hostElement.id === id);
  }

  private makeColumn(c: MarWijmoColumnDef): wjcGrid.Column {
    const col = new wjcGrid.Column();
    col.minWidth = c.width != null ? undefined : 120;
    col.binding = c.binding;
    col.header = c.header;
    col.width = c.width ?? '*';
    col.align = c.align as any;
    if (c.type === 'date' && !c.format) {
      col.format = 'dd/MM/yyyy';
    }
    col.allowSorting = c.allowSorting ?? true;
    col.allowResizing = c.allowResizing ?? true;
    col.allowDragging = c.allowDragging ?? true;
    col.allowDragging = c.allowDragging ?? true;
    col.isReadOnly = c.isReadOnly ?? true;
    col.dataMapEditor = c.dataMapEditor ?? undefined;
    col.dataType = c.dataType ?? undefined;

    if (c.sort !== undefined) {
      this.grid.collectionView.sortDescriptions.push(
        new wjcCore.SortDescription(
          c.binding,
          c.sort
        )
      );
    }

    switch (c.type) {
      case 'collection':
        const inputRef = this.getMultiSelectByBinding(col.binding);
        if (inputRef) {
          col.dataType = wjcCore.DataType.Array;
          col.editor = inputRef;
        }
        break;
      case 'number':
        col.format = c.format ?? 'n2';
        col.align = col.align ?? ('right' as any);
        break;
      case 'currency':
        col.format = c.format ?? 'c2';
        col.align = col.align ?? ('right' as any);
        break;
      case 'date':
        col.format = c.format ?? 'dd/MM/yyyy';
        break;
      case 'boolean':
        col.dataMap = new wjcGrid.DataMap([
          { key: true, value: 'Sim' },
          { key: false, value: 'Não' },
        ], 'key', 'value');
        break;
      default:
        if (c.format) col.format = c.format;
    }

    // dataMap custom (enums, etc.)
    if (c.dataMap?.length) {
      col.dataMap = new wjcGrid.DataMap(c.dataMap, 'key', 'value');
      if (c.type !== 'collection') {
        col.dataMapEditor = DataMapEditor.DropDownList;
      }
    }

    // valueFormatter opcional: registramos para aplicar via grid.formatItem
    if (c.valueFormatter) {
      this._valueFormatters.set(c.binding, c.valueFormatter);
    }
    return col;
  }

  // ============ Persistência de layout/filtros ============
  saveLayout(updateType) {
    if (!this.grid && !this.gridRefFilter && !this.state) return;
    const state: MarWijmoGridLayoutState = this.stateSvc.capture(this.grid, this.gridRefFilter, {
      tableName: this.options.tableName,
      frozenColumns: this.grid.frozenColumns ?? 0,
      id: this.id
    });
    this.callback.emit(state);
    this.stateSvc.save(state);
  }

  restoreLayout() {
    const saved = this.stateSvc.load(this.options.tableName);
    if (!saved) return;

    this.stateSvc.apply(this.grid, this.gridRefFilter, saved);
    // sincroniza Input com o que veio do estado
    this._frozenColumns = saved.frozenColumns ?? (this.options.frozenColumns ?? 0);
    this.grid.frozenColumns = this._frozenColumns;
  }

  resetLayout() {
    this.stateSvc.clear(this.options.tableName);
    this.buildColumns(this.grid);
    this.gridRefFilter.clear();
    this.grid.collectionView.sortDescriptions.clear();
    this.grid.frozenColumns = this.options.frozenColumns ?? 0;
    this.applyCollectionsFilter();
  }

  // ============ Exportações ============
  exportXlsx() {
    const name = `${ this.options.exportBaseName || 'dados' }.xlsx`;
    wjcXlsx.FlexGridXlsxConverter.saveAsync(this.grid, {
      includeColumnHeaders: true,
      includeCellStyles: true,
      formatItem: null
    }, name);
  }

  exportCsv() {
    if (!this.grid) return;

    const name = `${ this.options.exportBaseName || 'dados' }.csv`;

    const rng = new wjcGrid.CellRange(0, 0, this.grid.rows.length - 1, this.grid.columns.length - 1);

    const csv: string = (this.grid as any).getClipString(rng, true, false, true);

    const BOM = '\uFEFF';
    const blob = new Blob([BOM, csv], { type: 'text/csv;charset=utf-8;' });
    wjcCore.saveFile(blob, name);
  }

  exportPdf() {
    const name = `${ this.options.exportBaseName || 'dados' }.pdf`;
    const settings: wjcPdf.IFlexGridExportSettings = {
      scaleMode: wjcPdf.ScaleMode.PageWidth,
      documentOptions: {
        info: { title: name },
      },
      styles: {
        cellStyle: { backgroundColor: '#fff' },
        headerCellStyle: { backgroundColor: '#f2f2f2' }
      }
    };
    wjcPdf.FlexGridPdfConverter.export(this.grid, name, settings);
  }

  cellEditEnded(cell: wjcGrid.CellEditEndingEventArgs) {
    this.callbackEditEnding.emit({
      binding: cell.getColumn().binding,
      previousData: cell.previousData,
      dataItem: cell.getRow().dataItem,
    });
  }

  filterChange($event: any) {
    this.saveLayout('filterChanged');
    // this.cdr.detectChanges();
  }
}
